import React, { useEffect, useState } from 'react'
import { Box, Divider } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { ProviderDialog } from '../../system-container/provider-dialog/ProviderDialog'
import {
  HourSeriesFragment,
  LocalGridFragment,
  ProviderFragment,
} from '../../../generated/graphql'
import { useProviders } from '../../../provider/use-providers'
import {
  convertFormParametersToInputParameters,
  convertProviderParametersToFormParameters,
  FormValues,
} from '../../system-container/provider-dialog/form-values'
import { ProviderList } from '../../system-container/energy-system/ProviderList'
import {
  EnergyType,
  mapEnergyTypeToDemandType,
} from '../../../calculators/EnergyFlow'
import {
  localGridEnergyTypeVar,
  projectVar,
} from '../../../apollo/reactive-vars'
import {
  electricityProviderTypes,
  getProviderTypesByEnergyType,
  localEnergyProviderTypes,
  staticProviderTypes,
} from '../../../provider/provider-properties'
import { useAlert } from '../../alert/AlertProvider'
import { VisibleEnergyTypeSelect } from '../../system-container/VisibleEnergyTypeSelect'
import { LocalGridUsageMatrix } from './LocalGridUsageMatrix'
import { SingleSettingSection } from '../../common/SingleSettingSection'
import { LocalGridEnergySystemCharts } from './LocalGridEnergySystemCharts'
import { SpecialDemand } from '../../../calculators/special-demand-ids'
import { useLocalGridUtil } from '../../../local-grid/use-local-grid-util'
import { useAuthorization } from '../../../authorization/use-authorization'
import { sortProvidersByPriority } from '../../../calculators/providers/util/provider-utility'

type Props = {
  localGrid: LocalGridFragment
}

export function LocalGridEnergySystemTab({
  localGrid,
}: Props): React.ReactElement {
  const { createProvider, updateProvider, deleteProvider } = useProviders()
  const [isCreatingProvider, setIsCreatingProvider] = useState(false)
  const [editingProvider, setEditingProvider] = useState<
    ProviderFragment | undefined
  >()
  const project = useReactiveVar(projectVar)
  const localGridEnergyType = useReactiveVar(localGridEnergyTypeVar)
  const { t } = useTranslation()
  const { alert } = useAlert()
  const { isWaterGridConnected, availableEnergyTypes } =
    useLocalGridUtil(localGrid)
  const canEditProject = useAuthorization().canEditProject(project)

  const visibleProviderTypes = getProviderTypesByEnergyType(localGridEnergyType)
  const addableProviderTypes = visibleProviderTypes.filter(
    (type) =>
      !staticProviderTypes.includes(type) &&
      !localEnergyProviderTypes.includes(type),
  )

  useEffect(() => {
    if (!availableEnergyTypes.includes(localGridEnergyType)) {
      localGridEnergyTypeVar(availableEnergyTypes[0])
    }
  }, [availableEnergyTypes, localGridEnergyType])

  const onCreateSubmit = async (values: FormValues) => {
    if (!values.type) {
      return
    }

    setIsCreatingProvider(false)

    await createProvider(undefined, localGrid, {
      name: values.name ?? '',
      type: values.type,
      parameters: convertFormParametersToInputParameters(values.parameters),
      hourSeriesIds: values.hourSeriesIds ?? [],
      performanceTableIds: values.performanceTableIds ?? [],
      temperatureCurveIds: values.temperatureCurveIds ?? [],
      demands: [],
      providesElectricityForProviders: electricityProviderTypes.includes(
        values.type,
      ),
    })
  }

  const onEditSubmit = async (values: FormValues) => {
    if (!editingProvider) {
      return
    }

    setEditingProvider(undefined)

    await updateProvider(
      editingProvider.id,
      {
        name: values.name,
        type: values.type,
        parameters: convertFormParametersToInputParameters(values.parameters),
        hourSeriesIds: values.hourSeriesIds,
        performanceTableIds: values.performanceTableIds,
        temperatureCurveIds: values.temperatureCurveIds,
      },
      localGrid,
    )
  }

  const onDelete = () => {
    if (!editingProvider) {
      return
    }

    alert(
      t('localGrid.tabs.energySystem.deleteDialog.title'),
      t('localGrid.tabs.energySystem.deleteDialog.message'),
      [
        { title: t('common.cancel') },
        {
          title: t('localGrid.tabs.energySystem.deleteDialog.confirm'),
          color: 'secondary',
          onPress: async () => {
            setEditingProvider(undefined)
            await deleteProvider(undefined, localGrid, editingProvider.id)
          },
        },
      ],
    )
  }

  if (!project) {
    return <div />
  }

  const demands = Object.values(EnergyType).flatMap((energyType) => {
    const demandType = mapEnergyTypeToDemandType(energyType)

    const buildingsDemand = {
      id: SpecialDemand.Buildings,
      type: demandType,
      hourSeries: {
        name: t('localGrid.tabs.energySystem.buildings'),
      } as HourSeriesFragment,
    }

    if (!isWaterGridConnected) {
      return [buildingsDemand]
    }

    return [
      buildingsDemand,
      {
        id: SpecialDemand.LocalWaterGrid,
        type: demandType,
        hourSeries: {
          name: t('localGrid.tabs.energySystem.waterGrid'),
        } as HourSeriesFragment,
      },
    ]
  })

  const visibleProviders = localGrid.providers
    .filter((provider) => visibleProviderTypes.includes(provider.type))
    .sort(sortProvidersByPriority)

  const currentProviderNames = localGrid.providers.map(
    (provider) => provider.name,
  )

  return (
    <Box>
      <ProviderDialog
        isOpen={isCreatingProvider}
        defaultValues={{ parameters: {} }}
        projectId={project.id}
        providerTypes={addableProviderTypes}
        hourSeriesCollections={project.hourSeriesCollections}
        demands={demands}
        currentProviderNames={currentProviderNames}
        onClose={() => setIsCreatingProvider(false)}
        onSubmit={onCreateSubmit}
      />

      <ProviderDialog
        isOpen={editingProvider !== undefined}
        defaultValues={{
          name: editingProvider?.name,
          type: editingProvider?.type,
          parameters: convertProviderParametersToFormParameters(
            editingProvider?.parameters,
            demands,
          ),
          hourSeriesIds:
            editingProvider?.hourSeries.map((hourSeries) => hourSeries.id) ??
            [],
        }}
        projectId={project.id}
        providerTypes={visibleProviderTypes}
        hourSeriesCollections={project.hourSeriesCollections}
        demands={demands}
        isFallbackProvider={editingProvider?.isFallback}
        currentProviderNames={currentProviderNames}
        onClose={() => setEditingProvider(undefined)}
        onSubmit={onEditSubmit}
        onDelete={onDelete}
      />

      {availableEnergyTypes.length > 1 && (
        <>
          <SingleSettingSection title={t('localGrid.tabs.energySystem.type')}>
            <VisibleEnergyTypeSelect
              options={availableEnergyTypes}
              value={localGridEnergyType}
              onChange={(value) => localGridEnergyTypeVar(value)}
            />
          </SingleSettingSection>

          <Divider />
        </>
      )}

      <ProviderList
        providers={visibleProviders}
        onAdd={() => setIsCreatingProvider(true)}
        onEdit={(provider) => setEditingProvider(provider)}
        editDisabled={!canEditProject}
        energyType={localGridEnergyType}
      />

      <LocalGridUsageMatrix
        localGrid={localGrid}
        providers={visibleProviders}
        energyType={localGridEnergyType}
        demands={demands}
      />

      <Divider />

      <LocalGridEnergySystemCharts
        localGrid={localGrid}
        energyType={localGridEnergyType}
      />
    </Box>
  )
}
